import type { ReactNode } from 'react';
import type { PracticeFieldDef } from './fieldDefinitions';
import {
    normalizeFieldDisplayValue,
    resolveFieldOptions,
    withCurrentValueOption
} from './fieldDefinitions';
import { DetailField, type DetailDensity, type DetailFieldState } from './DetailField';

export interface DetailFieldGridProps {
    fields: PracticeFieldDef[];
    values: Record<string, string>;
    fieldState: (key: string) => DetailFieldState;
    fieldError: (key: string) => string | undefined;
    disabled: boolean;
    onFieldChange: (key: string, value: string) => void;
    /**
     * Defaults to 'comfortable' - every existing caller (the Details tab)
     * gets this for free and renders exactly as before. The create page is
     * the only 'compact' caller - see PracticeCreatePage.
     */
    density?: DetailDensity;
    /** Per-field accessory rendered below the field's box; only AccountSection sets it
     * (Bill.com and Spark actions), never the create page, which has no practiceId yet. */
    fieldAccessories?: Partial<Record<string, ReactNode>>;
    /**
     * Per-field adornment, keyed by field key - rendered inside that field's
     * control box while it holds a value (see DetailField's `adornment`).
     * Only ever set for the Practice card's phone field (a `tel:` CALL link)
     * - see InfoSection.
     */
    fieldAdornments?: Partial<Record<string, ReactNode>>;
    /** Show an asterisk on required fields' labels - see DetailField. */
    showRequiredMarkers?: boolean;
    /** Non-field cells laid out after the fields in the same grid (e.g. the iTero box). */
    extraCells?: ReactNode;
}

// The 12-column field grid, shared by a plain (no-subsection) card body and
// by each labelled subsection within a card - kept as its own component so
// neither DetailGroupCard nor DetailSubsection has to duplicate the
// per-field value/options wiring. Column count matches D2Field/D2Group in
// the design source - field width is a declared `span` (see
// fieldDefinitions.ts), not a fraction of the container.
export function DetailFieldGrid({
    fields,
    values,
    fieldState,
    fieldError,
    disabled,
    onFieldChange,
    density = 'comfortable',
    fieldAccessories,
    fieldAdornments,
    showRequiredMarkers = false,
    extraCells
}: DetailFieldGridProps) {
    return (
        <div
            className={`grid grid-cols-12 ${density === 'compact' ? 'gap-x-3 gap-y-2' : 'gap-x-4 gap-y-4'}`}
        >
            {fields.map((field) => {
                // Country's legacy spellings ("USA") display as their
                // canonical option instead of injecting a third one - see
                // normalizeFieldDisplayValue. Other fields pass through.
                const value = normalizeFieldDisplayValue(field, values[field.key] ?? '');
                // The dropdown must always include whatever value is
                // currently bound to it, even if that value isn't one of
                // the real hardcoded options (see withCurrentValueOption).
                const options = withCurrentValueOption(resolveFieldOptions(field), value);
                const deemphasized = field.deemphasizeWhenFieldEquals
                    ? values[field.deemphasizeWhenFieldEquals.field] ===
                      field.deemphasizeWhenFieldEquals.value
                    : false;
                return (
                    <DetailField
                        key={field.key}
                        field={field}
                        value={value}
                        state={fieldState(field.key)}
                        errorMessage={fieldError(field.key)}
                        options={options}
                        disabled={disabled}
                        density={density}
                        showRequiredMarker={showRequiredMarkers}
                        accessory={fieldAccessories?.[field.key]}
                        adornment={fieldAdornments?.[field.key]}
                        deemphasized={deemphasized}
                        onChange={(next) => onFieldChange(field.key, next)}
                    />
                );
            })}
            {extraCells}
        </div>
    );
}
